import { Link, Navigate, NavLink, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { Spinner } from './components/ui';
import { Landing } from './pages/Landing';
import { Start } from './pages/Start';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { EngagementDetail } from './pages/EngagementDetail';
import { NistBrowser } from './pages/NistBrowser';
import { Admin } from './pages/Admin';

function RequireAuth() {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <Spinner label="Checking session…" />;
  if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  return <CockpitLayout />;
}

function CockpitLayout() {
  const { user, logout } = useAuth();
  const navCls = ({ isActive }: { isActive: boolean }) =>
    `rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
      isActive ? 'bg-brand-500/15 text-brand-300' : 'text-slate-400 hover:text-slate-200'
    }`;
  return (
    <div className="min-h-full">
      <header className="sticky top-0 z-20 border-b border-slate-800 bg-slate-950/90 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center gap-4 px-4 py-3">
          <Link to="/app" className="flex items-center gap-2 font-bold text-slate-50">
            <span className="bg-gradient-to-r from-brand-400 to-cyan-400 bg-clip-text text-transparent">VibeProSoft</span>
            <span className="text-slate-300">Navigator</span>
          </Link>
          <nav className="flex items-center gap-1">
            <NavLink to="/app" end className={navCls}>Engagements</NavLink>
            <NavLink to="/app/nist" className={navCls}>NIST Library</NavLink>
            {(user?.role === 'admin' || user?.role === 'superadmin') && (
              <NavLink to="/app/admin" className={navCls}>Admin</NavLink>
            )}
          </nav>
          <div className="ml-auto flex items-center gap-3">
            <span className="hidden text-xs text-slate-500 sm:block">{user?.email}</span>
            <button onClick={() => void logout()} className="btn-secondary !px-3 !py-1 text-xs">Sign out</button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-4 py-6">
        <Outlet />
      </main>
    </div>
  );
}

export function App() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/start" element={<Start />} />
      <Route path="/login" element={<Login />} />
      <Route path="/app" element={<RequireAuth />}>
        <Route index element={<Dashboard />} />
        <Route path="engagements/:id" element={<EngagementDetail />} />
        <Route path="nist" element={<NistBrowser />} />
        <Route path="admin" element={<Admin />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
